import { useCallback } from 'react'

import {
  Item,
  ItemType,
  ItemsWithPagination,
  isFileItem
} from '@inno-sdks/files'
import { DEFAULT_SHORTCUT_ITEM_SIZE } from 'modules/desktop/constants'
import { getSystemShortcutDefinition } from 'modules/desktop/modules/files-integration/constants'
import {
  DesktopGridItem,
  ShortcutGridItem,
  SystemShortcutPresentation
} from 'shared/types/desktop-grid'

import { useDesktopStore } from '../../../stores'
import { useShortcutGroupStore } from '../../../stores/shortcutGroupStore'
import {
  forgetBaseLayoutEntriesForItemIds,
  pendingItemPositions,
  pendingMoveOutItems,
  placeItemsInGridSequentially
} from '../utils'
import { findNearestFreePosition } from '../utils/findNearestFreePosition'
import { getShortcutGroupCollisionItems } from '../utils/shortcutGroupBounds'

const getFileType = (item: Item): string => {
  if (item.type === ItemType.Folder) return 'folder'
  if (item.type === ItemType.Shortcut) return 'shortcut'
  if (isFileItem(item)) return item.metaData.mimeType
  return 'unknown'
}

const hasSameAction = (
  current: ShortcutGridItem['onClick'],
  expected: ShortcutGridItem['onClick']
): boolean => {
  if (current.type !== expected.type) return false

  if (current.type === 'openUrl' && expected.type === 'openUrl') {
    return current.url === expected.url
  }

  return (
    current.type === 'openApp' &&
    expected.type === 'openApp' &&
    current.appId === expected.appId &&
    current.url === expected.url
  )
}

const hasCanonicalSystemPresentation = (
  item: ShortcutGridItem,
  presentation: SystemShortcutPresentation
): boolean =>
  item.label === presentation.label &&
  item.subLabel === presentation.subLabel &&
  item.visualStyle === presentation.visualStyle &&
  item.isPinned === presentation.isPinned &&
  item.isPermanent === presentation.isPermanent &&
  item.iconType === presentation.iconType &&
  item.icon === presentation.icon &&
  // Only pinned shortcuts are layout anchors with an enforced position;
  // unpinned ones are free to move/reflow like any other shortcut.
  (!presentation.isPinned ||
    (item.position.row === presentation.position.row &&
      item.position.col === presentation.position.col)) &&
  item.size.width === presentation.size.width &&
  item.size.height === presentation.size.height &&
  hasSameAction(item.onClick, presentation.onClick)

const applySystemPresentation = (
  item: ShortcutGridItem,
  presentation: SystemShortcutPresentation
): ShortcutGridItem => {
  const common = {
    ...item,
    itemType: presentation.itemType,
    label: presentation.label,
    subLabel: presentation.subLabel,
    visualStyle: presentation.visualStyle,
    isPinned: presentation.isPinned,
    isPermanent: presentation.isPermanent,
    position: presentation.isPinned
      ? { ...presentation.position }
      : item.position,
    size: { ...presentation.size },
    onClick: { ...presentation.onClick }
  }

  switch (presentation.iconType) {
    case 'image':
      return { ...common, iconType: 'image', icon: presentation.icon }
    case 'file':
      return { ...common, iconType: 'file', icon: presentation.icon }
    case 'common':
      return { ...common, iconType: 'common', icon: presentation.icon }
  }
}

const applyServerPresentation = (
  item: ShortcutGridItem,
  serverItem: Item
): ShortcutGridItem => {
  const systemShortcut = getSystemShortcutDefinition(serverItem)
  if (systemShortcut) {
    if (hasCanonicalSystemPresentation(item, systemShortcut.presentation)) {
      return item
    }

    return applySystemPresentation(item, systemShortcut.presentation)
  }

  if (item.isPermanent === serverItem.extra?.isPermanent) return item
  return { ...item, isPermanent: serverItem.extra?.isPermanent }
}

type UseFilesListSyncOptions = {
  cols: number
  rows: number
  commitManualItems: (
    value:
      | DesktopGridItem[]
      | ((prevItems: DesktopGridItem[]) => DesktopGridItem[])
  ) => void
}

/**
 * Reconciles the desktop grid with the server's app-root listing (add new,
 * remove gone, sweep the in-flight move-out set). Returns the `onSuccess`
 * callback for `useFilesList`.
 */
export const useFilesListSync = ({
  cols,
  rows,
  commitManualItems
}: UseFilesListSyncOptions) => {
  const setItems = useDesktopStore(state => state.setItems)

  const addMissingItemsToDesktop = useCallback(
    (rootFolderItems: ItemsWithPagination) => {
      setItems(prevItems => {
        // Backfill server-owned presentation onto items restored from storage.
        // This also repairs stale system-shortcut metadata before layout reflow.
        const serverItemById = new Map(
          rootFolderItems.data.map(item => [item.id, item])
        )
        let presentationChanged = false
        const reconciled = prevItems.map(prev => {
          if (prev.itemType !== 'shortcut') return prev
          const serverItem = serverItemById.get(prev.fileId)
          if (!serverItem) return prev
          const next = applyServerPresentation(prev, serverItem)
          if (next !== prev) presentationChanged = true
          return next
        })
        const nextPrevItems = presentationChanged ? reconciled : prevItems
        const groupBoundaryObstacles = getShortcutGroupCollisionItems(
          useShortcutGroupStore.getState().groups,
          nextPrevItems
        )

        const missingItems = rootFolderItems.data.filter(
          item =>
            !nextPrevItems.some(
              i => i.itemType === 'shortcut' && i.fileId === item.id
            ) && !pendingMoveOutItems.has(item.id)
        )

        if (missingItems.length === 0) return nextPrevItems

        forgetBaseLayoutEntriesForItemIds(
          missingItems.map(item => item.id.toString())
        )

        const newItems: ShortcutGridItem[] = missingItems.map(
          (item): ShortcutGridItem => {
            const systemShortcut = getSystemShortcutDefinition(item)
            const fallbackItem: ShortcutGridItem = {
              id: item.id.toString(),
              fileId: item.id,
              itemType: 'shortcut',
              position: { row: 0, col: 0 },
              size: DEFAULT_SHORTCUT_ITEM_SIZE,
              label: item.name,
              iconType: 'common',
              icon: 'HourglassLineIcon',
              onClick: {
                type: 'openUrl',
                url: 'https://example.com'
              },
              createdAt: item.createdAt,
              fileType: getFileType(item),
              isPermanent: item.extra?.isPermanent
            }

            return systemShortcut
              ? applySystemPresentation(
                  fallbackItem,
                  systemShortcut.presentation
                )
              : fallbackItem
          }
        )

        const pinnedItems = newItems.filter(item => item.isPinned)
        const placedAtDropTargets: ShortcutGridItem[] = []
        const remaining: ShortcutGridItem[] = []

        for (const item of newItems.filter(item => !item.isPinned)) {
          const preferred = pendingItemPositions.consume(item.fileId)
          if (!preferred) {
            remaining.push(item)
            continue
          }

          const occupied = [
            ...nextPrevItems,
            ...groupBoundaryObstacles,
            ...placedAtDropTargets
          ]
          const target = findNearestFreePosition(
            occupied,
            cols,
            rows,
            item.size,
            preferred
          )
          if (target) {
            placedAtDropTargets.push({ ...item, position: target })
          } else {
            remaining.push(item)
          }
        }

        const sequentiallyPlaced = placeItemsInGridSequentially(
          remaining,
          [
            ...nextPrevItems,
            ...groupBoundaryObstacles,
            ...pinnedItems,
            ...placedAtDropTargets
          ],
          cols,
          rows
        )

        return [
          ...nextPrevItems,
          ...pinnedItems,
          ...placedAtDropTargets,
          ...sequentiallyPlaced
        ]
      })
    },
    [setItems, cols, rows]
  )

  const removeStaleItemsFromDesktop = useCallback(
    (items: ItemsWithPagination) => {
      const currentFileIds = items.data.map(item => item.id)
      commitManualItems(prevItems => {
        const next = prevItems.filter(
          item =>
            // widgets aren't files-list-backed. Only sweep shortcuts whose
            // file has disappeared from the listing.
            item.itemType !== 'shortcut' || currentFileIds.includes(item.fileId)
        )
        // Preserve identity when nothing was swept — a changed reference
        // makes commitManualItems replace the whole base layout with the
        // items' current (possibly mid-reflow) positions on every fetch.
        return next.length === prevItems.length ? prevItems : next
      })
    },
    [commitManualItems]
  )

  return useCallback(
    (items: ItemsWithPagination) => {
      addMissingItemsToDesktop(items)
      removeStaleItemsFromDesktop(items)
      // Drop in-flight move-out IDs the server has applied (gone from the list).
      pendingMoveOutItems.reconcile(new Set(items.data.map(i => i.id)))
    },
    [addMissingItemsToDesktop, removeStaleItemsFromDesktop]
  )
}
